<template>
  <div class="min-h-screen w-full flex flex-col md:flex-row bg-white font-sans overflow-hidden" v-loading="loading" element-loading-background="rgba(255, 255, 255, 0.8)">
    
    <!-- LEFT PANEL (Brand Showcase) -->
    <div class="relative hidden md:flex w-1/2 min-h-screen bg-gradient-to-br from-brand via-brand-dark to-[#01255e] flex-col justify-between p-12 overflow-hidden items-start">
      <!-- Dynamic Background Orbs -->
      <div class="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div class="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-[#4f46e5] rounded-full mix-blend-screen filter blur-[150px] opacity-40 animate-float-slow"></div>
        <div class="absolute bottom-[-10%] right-[-10%] w-[600px] h-[600px] bg-[#00a8ff] rounded-full mix-blend-screen filter blur-[150px] opacity-30 animate-float-delayed"></div>
      </div>
      
      <!-- Top Logo area -->
      <div class="relative z-10 flex items-center gap-3">
        <div class="w-12 h-12 rounded-xl shadow-lg flex items-center justify-center p-1 border border-white/20">
          <img class="max-h-full max-w-full object-contain rounded-lg drop-shadow" :src="$app.theme.loginLogoUrl || $app.theme.logoUrl || require('../../assets/home-logo.png')" alt="HIGHLAND AGRI Logo" />
        </div>
        <div class="text-white font-bold text-[18px] tracking-wide">
          {{ $app.theme.appName || 'HIGHLAND AGRI' }}
        </div>
      </div>

      <!-- Center Hero Text -->
      <div class="relative z-10 w-full max-w-lg mb-20">
        <div class="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-yellow-400 font-semibold text-[13px] tracking-wider uppercase mb-6">
          {{ $app.theme.loginPortalLabel || 'Portal Management' }}
        </div>
        <h1 class="text-[42px] leading-[1.15] font-extrabold text-white tracking-tight mb-6">
          {{ $app.theme.loginTitle || 'Hệ Thống Phiếu Chi Điện Tử' }}
        </h1>
        <p class="text-[16px] text-white/80 leading-relaxed font-medium">
          {{ $app.theme.loginSubtitle || 'Giải pháp quản lý tài chính toàn diện.' }}
        </p>
      </div>

      <!-- Bottom Footer -->
      <div class="relative z-10 w-full flex items-center justify-between text-white/50 text-[13px] font-medium border-t border-white/10 pt-6">
        <span>{{ $app.theme.loginCopyright || '© 2024 HIGHLAND AGRI. All rights reserved.' }}</span>
        <div class="flex gap-4">
          <a href="#" class="hover:text-white transition-colors">Privacy</a>
          <a href="#" class="hover:text-white transition-colors">Terms</a>
        </div>
      </div>
    </div>

    <!-- RIGHT PANEL (Login Form) -->
    <div class="relative w-full md:w-1/2 min-h-screen flex items-center justify-center bg-white p-6 md:p-12 z-20 shadow-[-10px_0_30px_rgba(0,0,0,0.05)]">
      
      <!-- Mobile Top Logo (Only visible on small screens) -->
      <div class="absolute top-8 left-8 flex md:hidden items-center gap-3">
         <img class="h-[28px] object-contain" :src="$app.theme.loginLogoUrl || $app.theme.logoUrl || require('../../assets/home-logo.png')" alt="HIGHLAND AGRI Logo" />
         <span class="font-bold text-gray-800 text-[16px]">{{ $app.theme.appName }}</span>
      </div>

      <div class="w-full max-w-[400px]">
        
        <div class="mb-8 text-center md:text-left">
          <h2 class="text-[28px] font-extrabold text-gray-900 tracking-tight mb-2">Chào mừng trở lại</h2>
          <p class="text-gray-500 font-medium text-[15px]">Đăng nhập vào không gian làm việc của bạn</p>
        </div>

        <!-- Login Options -->
        <div class="space-y-5">
          <!-- Form đăng nhập bằng Email -->
          <div class="space-y-4">
            <div class="space-y-2">
              <label class="text-sm font-medium text-gray-700">Email</label>
              <input v-model="email" type="email" placeholder="Nhập email của bạn" class="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand transition-colors" />
            </div>
            <div class="space-y-2">
              <label class="text-sm font-medium text-gray-700">Mật khẩu</label>
              <div class="relative">
                <input v-model="password" :type="showPassword ? 'text' : 'password'" placeholder="Nhập mật khẩu" class="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-brand focus:border-brand transition-colors pr-12" @keyup.enter="loginWithEmail" />
                <div class="absolute inset-y-0 right-0 flex items-center pr-4 cursor-pointer text-gray-400 hover:text-gray-600" @click="showPassword = !showPassword">
                    <font-awesome-icon :icon="showPassword ? ['fas', 'eye-slash'] : ['fas', 'eye']" />
                </div>
              </div>
            </div>
            <button @click="loginWithEmail" :disabled="loading || !email || !password"
              class="w-full relative overflow-hidden group/btn inline-flex items-center justify-center gap-3 h-[50px] mt-2
                     text-white text-[16px] font-bold rounded-xl
                     bg-brand border border-transparent
                     shadow-[0_4px_14px_0_rgba(2,75,179,0.39)]
                     hover:shadow-[0_6px_20px_rgba(2,75,179,0.23)] hover:-translate-y-[1px]
                     disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none disabled:transform-none disabled:cursor-not-allowed
                     transition-all duration-200">
               <span class="relative z-10">Đăng nhập</span>
            </button>
          </div>

          <div class="relative flex items-center py-2" v-if="enableSuiteLogin">
            <div class="flex-grow border-t border-gray-200"></div>
            <span class="flex-shrink-0 mx-4 text-gray-400 text-sm">Hoặc</span>
            <div class="flex-grow border-t border-gray-200"></div>
          </div>

          <!-- SSO Redirect Button -->
          <button v-if="enableSuiteLogin" @click="loginBySuite" :disabled="loading" 
            class="w-full relative overflow-hidden group/btn inline-flex items-center justify-center gap-3 h-[50px] mt-6
                   text-white text-[16px] font-bold rounded-xl
                   bg-brand border border-transparent
                   shadow-[0_4px_14px_0_rgba(2,75,179,0.39)]
                   hover:shadow-[0_6px_20px_rgba(2,75,179,0.23)] hover:-translate-y-[1px]
                   disabled:bg-gray-300 disabled:text-gray-500 disabled:shadow-none disabled:transform-none disabled:cursor-not-allowed
                   transition-all duration-200">
             <span class="relative z-10">Đăng nhập bằng HIGHLAND AGRI Suite</span>
          </button>
        </div>

        <div class="mt-8 flex items-center justify-center md:justify-start gap-2 text-[13px] text-gray-400 font-medium">
          <font-awesome-icon :icon="['fas', 'shield-halved']" />
          <span>Bảo mật bởi HIGHLAND AGRI Suite Workspace</span>
        </div>
      </div>

    </div>
  </div>
</template>


<script>
const API_URL = process.env.VUE_APP_API_URL
const CLIENT_ID = process.env.VUE_APP_GG_CLIENT_ID
import { saveToStorage } from "@/utils/storage";
export default {
  components: {
  },
  data() {
    return {
      loading: false,
      email: '',
      password: '',
      showPassword: false,
      enableSuiteLogin: process.env.VUE_APP_ENABLE_SUITE_LOGIN !== 'false', // Mặc định bật hoặc theo env
    }
  },
  mounted() {
    this.$app.fetchTheme();
    const urlParams = new URLSearchParams(window.location.search);
    const ticket = urlParams.get('ticket');
    if (ticket) {
      this.verifyTicket(ticket);
    }
  },
  methods: {
    async verifyTicket(ticket) {
      this.loading = true;
      try {
        const result = await this.$axios.post(`${API_URL}/authuserslog/sso-login`, { ticket });
        if (result.data.success) {
          this.$app.userInfo = result.data.userInfo;
          saveToStorage("uToken", JSON.stringify(result.data.token));
          this.$app.token = result.data.token;
          this.$emit('issuccesslogin');
          this.$message({
            message: 'Đăng nhập thành công',
            type: 'success'
          });
          
          this.$router.push('/').catch(()=>{});
        } else {
          this.$message({
            message: result.data.message || 'Lỗi đăng nhập',
            type: 'error'
          });
        }
      } catch (err) {
        this.$message({
          message: err.response?.data?.message || 'Lỗi đăng nhập',
          type: 'error'
        });
      }
      this.loading = false;
    },
    async loginBySuite() {
      if (this.loading) return;
      this.loading = true;
      const suiteUrl = process.env.VUE_APP_SUITE_URL || 'https://lab-suite.guta.asia';
      const redirectUri = window.location.origin + '/sso/callback';
      // Truyền redirect_uri ra trước dấu # để đảm bảo tương thích mọi cơ chế đọc param
      window.location.href = `${suiteUrl}/?redirect_uri=${encodeURIComponent(redirectUri)}#/login?redirect_uri=${encodeURIComponent(redirectUri)}`;
    },
    async loginWithEmail() {
      if (!this.email || !this.password) return;
      this.loading = true;
      try {
        const res = await this.$axios.post(`${API_URL}/authuserslog/login-email`, {
          email: this.email,
          password: this.password
        });
        
        if (res.data.success) {
          this.$app.userInfo = res.data.userInfo;
          saveToStorage("uToken", JSON.stringify(res.data.token));
          this.$app.token = res.data.token;
          this.$emit('issuccesslogin');
          this.$message({
            message: 'Đăng nhập thành công',
            type: 'success'
          });
          this.$router.push('/').catch(()=>{});
        } else {
          this.$notify.error({
            title: 'Lỗi đăng nhập',
            message: res.data.message || 'Tài khoản không tồn tại hoặc sai mật khẩu.'
          });
        }
      } catch (err) {
        this.$notify.error({
          title: 'Lỗi đăng nhập',
          message: err.response?.data?.message || err.message
        });
      } finally {
        this.loading = false;
      }
    }

  },
};
</script>

<style scoped>
@keyframes float {
  0% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-30px) scale(1.05); }
  100% { transform: translateY(0) scale(1); }
}

@keyframes float-slow {
  0% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(30px, -50px) scale(1.1); }
  66% { transform: translate(-20px, 20px) scale(0.95); }
  100% { transform: translate(0, 0) scale(1); }
}

@keyframes float-delayed {
  0% { transform: translate(0, 0) scale(1); }
  33% { transform: translate(-40px, -30px) scale(0.9); }
  66% { transform: translate(20px, -60px) scale(1.05); }
  100% { transform: translate(0, 0) scale(1); }
}

.animate-float {
  animation: float 10s ease-in-out infinite;
}
.animate-float-slow {
  animation: float-slow 15s ease-in-out infinite;
}
.animate-float-delayed {
  animation: float-delayed 18s ease-in-out infinite;
  animation-delay: 2s;
}
</style>
<style lang="scss" scoped>
.el-input {
  input.el-input__inner {
    height: 50px;
  }
}


@keyframes sheen-move {
  0% {
    left: -150%;
  }

  100% {
    left: 150%;
  }
}

@keyframes glassshine {
  0% {
    transform: translateX(-150%) rotate(45deg);
  }

  50% {
    transform: translateX(0%) rotate(45deg);
  }

  100% {
    transform: translateX(150%) rotate(45deg);
  }
}

.animate-glassshine {
  animation: glassshine 3s ease-in-out infinite;
  mix-blend-mode: screen;
}
</style>